import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { List, Notebook } from 'lucide-react'
import { cn } from '@/lib/utils'
import { getStatusPillClass } from './RecoveryTrackingHeader'
import type { RequestObjectDetailRow } from '../api'
import { formatDateForLocale } from '@/lib/dateUtils'
import { buildIconPath } from '@/lib/imagePaths'
import { TranslatableText } from '@/components/ui/TranslatableText'

function formatDateTime(iso: string | null | undefined, locale: string): string {
    if (!iso) return '—'
    try {
        return formatDateForLocale(iso, locale, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
    } catch {
        return String(iso)
    }
}

function DoughnutChart({ percentage, size = 36 }: { percentage: number; size?: number }) {
    const r = (size - 4) / 2
    const circumference = 2 * Math.PI * r
    const pct = Math.min(100, Math.max(0, percentage))
    const strokeDashoffset = circumference * (1 - pct / 100)
    return (
        <div className="relative flex-shrink-0" style={{ width: size, height: size }} title={`${Math.round(pct)}%`}>
            <svg width={size} height={size} className="rotate-[-90deg]">
                <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="currentColor" strokeWidth={3} className="text-muted-foreground/30" />
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={r}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={3}
                    strokeDasharray={circumference}
                    strokeDashoffset={strokeDashoffset}
                    strokeLinecap="round"
                    className="text-green-600 transition-all duration-300"
                />
            </svg>
            <span className="absolute inset-0 flex items-center justify-center text-[10px] font-medium text-foreground">{Math.round(pct)}%</span>
        </div>
    )
}

function getAnalyticsCircle(analytics: number | string | null | undefined) {
    const raw = analytics ?? null
    const v = raw !== null && raw !== undefined && raw !== '' ? Number(raw) : null
    const num = typeof v === 'number' && !Number.isNaN(v) ? v : null
    const color =
        num === null ? 'grey'
        : num === 0 || num === 4 ? 'green'
        : num === 1 || num === 3 ? 'amber'
        : num === 2 ? 'red'
        : 'grey'
    const colorClasses = { green: 'bg-green-500', amber: 'bg-amber-500', red: 'bg-red-500', grey: 'bg-gray-500' }
    const pulseClass = color === 'red' ? 'animate-analytics-pulse-red' : color === 'amber' ? 'animate-analytics-pulse-amber' : ''
    return (
        <div
            className={cn('h-4 w-4 shrink-0 rounded-full', colorClasses[color as keyof typeof colorClasses], pulseClass)}
            title={num !== null ? String(num) : '—'}
        />
    )
}

function TaskStatsCell({ row }: { row: RequestObjectDetailRow }) {
    const noTasks = row.totalTasks ?? 0
    const noCompleted = row.completedTasks ?? 0
    const pct = row.percentComplete ?? (noTasks > 0 ? (noCompleted / noTasks) * 100 : 0)
    return <DoughnutChart percentage={pct} size={36} />
}

export interface RecoverySetObjectsTableProps {
    rows: RequestObjectDetailRow[]
    isLoading: boolean
    isError: boolean
    /** When true, render in a compact card (e.g. for Objects tab). When false, no outer padding (e.g. inside dialog). */
    inCard?: boolean
    /** Locale for date formatting */
    locale?: string
    /** When provided, show Actions column with View Runbook and Updates buttons */
    onViewRunbook?: (row: RequestObjectDetailRow) => void
    onOpenUpdates?: (recoverRequestId: number | null) => void
}

export function RecoverySetObjectsTable({
    rows,
    isLoading,
    isError,
    inCard = false,
    locale = 'en-GB',
    onViewRunbook,
    onOpenUpdates,
}: RecoverySetObjectsTableProps) {
    const { t } = useTranslation()
    const showActions = onViewRunbook != null || onOpenUpdates != null

    const tableContent = (
        <table className="w-full text-sm min-w-[1000px]">
            <thead>
                <tr className="border-b bg-muted/50">
                    <th className="text-left p-3 font-medium">{t('recoverRequests.objectID', 'Object ID')}</th>
                    <th className="text-left p-3 font-medium">{t('recoverRequests.waitGo', 'Wait/Go')}</th>
                    <th className="text-left p-3 font-medium">{t('recoverRequests.forecastStartTime', 'Forecast start')}</th>
                    <th className="text-left p-3 font-medium">{t('recoverRequests.forecastFinishTime', 'Forecast finish')}</th>
                    <th className="text-left p-3 font-medium">{t('recoverRequests.isComplete', 'IsComplete')}</th>
                    <th className="text-left p-3 font-medium">{t('recoverRequests.actualStartTime', 'Actual start time')}</th>
                    <th className="text-left p-3 font-medium">{t('recoverRequests.tasksProgress', 'Tasks / Progress')}</th>
                    <th className="text-left p-3 font-medium">{t('recoverRequests.engineerName', 'Engineer name')}</th>
                    <th className="text-left p-3 font-medium">{t('recoverRequests.assignmentStatus', 'Assignment status')}</th>
                    <th className="text-left p-3 font-medium">{t('recoverRequests.locationDescription', 'Location')}</th>
                    {showActions && (
                        <th className="text-right p-3 font-medium">{t('common.actions', 'Actions')}</th>
                    )}
                </tr>
            </thead>
            <tbody>
                {rows.map((row, idx) => (
                    <tr key={row.recoveryHierarchyId ?? `${row.requestID}-${row.objectID}-${idx}`} className="border-b last:border-0 hover:bg-muted/30">
                        <td className="p-3">
                            <div className="flex items-center gap-2 min-w-0" title={[row.objectID, row.objectDescription].filter(Boolean).join(' — ') || undefined}>
                                <div className="flex flex-col min-w-0 flex-1">
                                    <span className="text-sm font-medium truncate">{row.objectID ?? '—'}</span>
                                    {row.objectDescription != null && row.objectDescription !== '' && (
                                        <span className="text-xs text-muted-foreground truncate"><TranslatableText text={row.objectDescription} /></span>
                                    )}
                                </div>
                                {row.iconFilename && (
                                    <img src={buildIconPath(row.iconFilename)} alt="" className="h-7 w-7 object-contain shrink-0" />
                                )}
                            </div>
                        </td>
                        <td className="p-3">
                            <span
                                className={cn(
                                    'inline-flex px-2 py-0.5 rounded text-xs font-medium',
                                    (row.waitGo?.toLowerCase() ?? '') === 'go' ? 'bg-green-500/20 text-green-800 dark:text-green-200' : 'bg-red-500/20 text-red-800 dark:text-red-200'
                                )}
                            >
                                {row.waitGo === 'Go' || (row.waitGo?.toLowerCase() ?? '') === 'go' ? 'Go' : 'Wait'}
                            </span>
                        </td>
                        <td className="p-3 text-muted-foreground text-xs">
                            {row.forecastStartTime ? formatDateTime(row.forecastStartTime, locale) : '—'}
                        </td>
                        <td className="p-3 text-muted-foreground text-xs">
                            {row.forecastFinishTime ? formatDateTime(row.forecastFinishTime, locale) : '—'}
                        </td>
                        <td className="p-3">
                            {row.isComplete === 1 ? (
                                <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-green-500/20 text-green-800 dark:text-green-200">
                                    {t('recoverRequests.complete', 'Complete')}
                                </span>
                            ) : (
                                '—'
                            )}
                        </td>
                        <td className="p-3 text-muted-foreground text-xs">
                            {row.actualStartTime ? formatDateTime(row.actualStartTime, locale) : '—'}
                        </td>
                        <td className="p-3">
                            <TaskStatsCell row={row} />
                        </td>
                        <td className="p-3">
                            {row.engineerName != null && (row.engineerName ?? '').trim() !== '' ? (
                                <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-primary/15 text-primary dark:bg-primary/25 dark:text-primary-foreground">
                                    <TranslatableText text={row.engineerName} />
                                </span>
                            ) : (
                                '—'
                            )}
                        </td>
                        <td className="p-3">
                            {row.assignmentStatus != null && (row.assignmentStatus ?? '').trim() !== '' ? (
                                <span className={cn('inline-flex px-2 py-0.5 rounded text-xs font-medium', getStatusPillClass(row.assignmentStatus!))}>
                                    {row.assignmentStatus}
                                </span>
                            ) : (
                                '—'
                            )}
                        </td>
                        <td className="p-3 text-muted-foreground text-xs">
                            <span className="truncate block max-w-[160px]" title={row.locationDescription ?? undefined}>{row.locationDescription ? <TranslatableText text={row.locationDescription} /> : '—'}</span>
                        </td>
                        {showActions && (
                            <td className="p-3 text-right">
                                <div className="flex items-center justify-end gap-1">
                                    {(row.totalTasks ?? 0) > 0 && onViewRunbook && (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-8 w-8 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                onViewRunbook(row)
                                            }}
                                            aria-label={t('recoverRequests.viewRunbook', 'View Runbook')}
                                        >
                                            <List className="h-4 w-4" />
                                        </Button>
                                    )}
                                    {onOpenUpdates && (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-8 w-8 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                onOpenUpdates(row.recoverRequestID ?? null)
                                            }}
                                            aria-label={t('recoverRequests.updates', 'Updates')}
                                        >
                                            <Notebook className="h-4 w-4" />
                                        </Button>
                                    )}
                                </div>
                            </td>
                        )}
                    </tr>
                ))}
            </tbody>
        </table>
    )

    const content = (
        <div className={cn('rounded-md border overflow-hidden min-w-0 overflow-x-auto', inCard && 'border-0')}>
            {isLoading && (
                <div className="p-4 text-sm text-muted-foreground border-b">{t('common.loading', 'Loading…')}</div>
            )}
            {isError && (
                <div className="p-4 text-sm text-destructive border-b">{t('common.error', 'Failed to load.')}</div>
            )}
            {!isLoading && !isError && rows.length === 0 && (
                <div className="p-4 text-sm text-muted-foreground">{t('recoverRequests.noObjectDetails', 'No object details.')}</div>
            )}
            {!isLoading && !isError && rows.length > 0 && tableContent}
        </div>
    )

    if (inCard) {
        return (
            <div className="rounded-lg border bg-card overflow-hidden max-h-[70vh] flex flex-col">
                <div className="min-h-0 overflow-y-auto flex-1">{content}</div>
            </div>
        )
    }
    return content
}
